import {
  Anchor,
  Badge,
  Group,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrgs } from '../api/orgs';
import { useClinicalPatients } from '../api/platform-users';
import { SearchLoading } from '../components/search-loading';

/**
 * Cross-organisation, read-only listing of clinical patients — the same
 * records surfaced in the Users page's "Clinical patients" section, promoted
 * to their own page. Org is an optional filter; with none selected the list
 * spans every organisation. No Promote action (that stays on the Users page).
 */
export function PatientsPage() {
  const [params, setParams] = useSearchParams();
  const { data: orgs } = useOrgs();

  const org = params.get('org') ?? '';
  const searchParam = params.get('search') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  const [searchInput, setSearchInput] = useState(searchParam);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  // Sync the debounced search back to the URL without nuking other params.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    if (debouncedSearch !== searchParam) {
      next.set('page', '1');
      setParams(next, { replace: true });
    }
  }, [debouncedSearch]);

  const query = useClinicalPatients({
    org: org || undefined,
    search: debouncedSearch || undefined,
    page,
  });

  const orgNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgs ?? []) m.set(o.id, o.name);
    return m;
  }, [orgs]);

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  function patchParams(patch: Record<string, string>, resetPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.set('page', '1');
    setParams(next, { replace: true });
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Patients</Title>

      <Group gap="md" align="flex-end" wrap="wrap">
        <Select
          label="Organisation"
          placeholder="All organisations"
          value={org || null}
          onChange={(v) => patchParams({ org: v ?? '' }, true)}
          data={(orgs ?? []).map((o) => ({ value: o.id, label: o.name }))}
          searchable
          clearable
          comboboxProps={{ keepMounted: false }}
          w={260}
        />
        <TextInput
          label="Search"
          placeholder="name or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          w={240}
        />
      </Group>

      {query.isPending ? (
        <SearchLoading />
      ) : (
        <>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Organisation</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>DOB</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Products</Table.Th>
                <Table.Th>Cases</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(query.data?.items ?? []).map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>{orgNameById.get(p.organisation_id) ?? p.organisation_id}</Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={`/patients/${encodeURIComponent(p.id)}`}>
                      {p.given_name} {p.family_name}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>{p.dob}</Table.Td>
                  <Table.Td>{p.email ?? '—'}</Table.Td>
                  <Table.Td>
                    {p.products.length === 0 ? (
                      <Text c="dimmed" size="sm">
                        —
                      </Text>
                    ) : (
                      <Group gap={4} wrap="wrap">
                        {p.products.map((prod) => (
                          <Badge
                            key={prod.id}
                            variant="light"
                            color="blue"
                            title={prod.display_name}
                          >
                            {prod.code}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {p.case_count > 0 ? (
                      <Anchor
                        component={Link}
                        size="sm"
                        to={`/cases?patient_id=${encodeURIComponent(p.id)}&org_id=${encodeURIComponent(p.organisation_id)}`}
                      >
                        {p.case_count}
                      </Anchor>
                    ) : (
                      <Text size="sm">{p.case_count}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={p.status === 'active' ? 'green' : 'gray'} variant="light">
                      {p.status}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
              {(query.data?.items ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center">
                      No patients match the current filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          <Group justify="space-between">
            <Text c="dimmed" size="sm">
              {query.data && query.data.grand_total > 0
                ? debouncedSearch.trim()
                  ? `${total} matching of ${query.data.grand_total} patients`
                  : `${total} patients total`
                : `${total} total`}
            </Text>
            <Pagination
              value={page}
              total={totalPages}
              onChange={(p) => patchParams({ page: String(p) })}
              size="sm"
            />
          </Group>
        </>
      )}
    </Stack>
  );
}
